
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Card,
    CardBody,
    Col,
    Form,
    Input,
    Label,
    Row,
    FormFeedback,
    Spinner,
    Container,
    CardHeader
} from "reactstrap";
import { Btn, H5 } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, uploadFile, mediaUrl } from "../../Utils";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from "../../Context/AuthContext";

interface FormValues {
    branch_id: string;
    vehicle_id: string;
    insurance_company: string;
    policy_number: string;
    start_date: string;
    expiry_date: string;
    premium_amount: string;
    policy_document: FileList;
    remarks?: string;
}

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export default function EditInsuranceRenewal() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [branches, setBranches] = useState<any[]>([]);
    const [vehicles, setVehicles] = useState<any[]>([]);
    const [docPreview, setDocPreview] = useState<string | null>(null);
    const { hasPermission } = useAuth();
    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
        reset
    } = useForm<FormValues>();

    const selectedBranch = watch("branch_id");
    const watchedStart = watch("start_date");
    const watchedExpiry = watch("expiry_date");

    // Fetch Branches
    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const branchRes: any = await apiPost("branches/list", { status: 'Active' });
                setBranches(branchRes);
            } catch (error) {
                console.error(error);
            }
        };
        fetchBranches();
    }, []);

    // Fetch Vehicles when Branch changes
    useEffect(() => {
        const fetchVehicles = async () => {
            if (!selectedBranch) {
                setVehicles([]);
                return;
            }
            try {
                const response: any = await apiPost("vehicles/list", { branch_id: selectedBranch });
                if (Array.isArray(response)) {
                    const activeVehicles = response.filter((v: any) => v.status !== 'Sold');
                    setVehicles(activeVehicles);
                } else if (response.data) {
                    const activeVehicles = Array.isArray(response.data) ? response.data.filter((v: any) => v.status !== 'Sold') : [];
                    setVehicles(activeVehicles);
                } else {
                    setVehicles([]);
                }
            } catch (error) {
                console.error(error);
                setVehicles([]);
            }
        };
        fetchVehicles();
    }, [selectedBranch]);

    // Fetch Data
    useEffect(() => {
        const fetchData = async () => {
            if (!location.state || !location.state.id) {
                toast.error("No Renewal ID found");
                navigate(`${process.env.PUBLIC_URL}/insurance_renewal`);
                return;
            }

            try {
                const result = await apiPost("insurance_renewal/viewbyid", { id: location.state.id });

                // Wait for branches/vehicles? Effect timing might be tricky. 
                // We should rely on standard flow. If branches fetched, then we set value.
                // Or simply set values, and when dropdowns load they will match.

                setValue("branch_id", result.branch_id);
                // Need to trigger vehicle fetch? The useEffect[selectedBranch] will trigger but might clear vehicle_id initially.
                // We might need to manually set vehicle list or wait.
                // For simplicity, let's load vehicles for this branch manually here to ensure it's ready.

                const vehiclesData: any = await apiPost("vehicles/list", { branch_id: result.branch_id });
                if (Array.isArray(vehiclesData)) setVehicles(vehiclesData);
                else if (vehiclesData.data) setVehicles(vehiclesData.data);

                setValue("vehicle_id", result.vehicle_id);
                setValue("insurance_company", result.insurance_company);
                setValue("policy_number", result.policy_number);
                setValue("start_date", result.start_date);
                setValue("expiry_date", result.expiry_date);
                setValue("premium_amount", result.premium_amount);
                setValue("remarks", result.remarks);

                if (result.policy_document) {
                    setDocPreview(`${mediaUrl}${result.policy_document}`);
                }

                setLoading(false);
            } catch (error) {
                console.error(error);
                toast.error("Failed to fetch details");
                setLoading(false);
            }
        };
        fetchData();
    }, [location.state, navigate, setValue]);


    const onSubmit = async (data: FormValues) => {
        setLoading(true);
        try {
            let policyDocName = "";
            if (data.policy_document && data.policy_document.length > 0) {
                const file = data.policy_document[0];
                if (file.size > 2 * 1024 * 1024) {
                    toast.error("File size must be less than 2MB");
                    setLoading(false);
                    return;
                }
                policyDocName = await uploadFile(file);
            }

            const payload = {
                id: location.state.id,
                ...data,
                policy_document: policyDocName, // Backend should handle keep existing if null/empty string passed, OR we filter it out
                // Actually my backend controller updates with request->all().
                // If I send empty string for policy_document, it might overwrite.
                // Better logic: if policyDocName is empty, don't send it? 
                // Or backend model 'fillable' allows it.
                // Let's check Controller. It uses update($request->all()). 
                // So if we don't include it in payload, it won't be updated (if we construct payload manually).
            };

            // Refine payload
            const finalPayload: any = { ...data, id: location.state.id };
            if (policyDocName) {
                finalPayload.policy_document = policyDocName;
            } else {
                delete finalPayload.policy_document; // Don't send if not updating
            }
            // Remove FileList object
            if (finalPayload.policy_document instanceof FileList) delete finalPayload.policy_document;

            await apiPost("insurance_renewal/update", finalPayload);
            toast.success("Insurance Renewal updated successfully", { autoClose: 1000 });
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/insurance_renewal`);
            }, 1000);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error updating insurance renewal");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Edit Insurance Renewal" parent="Renewal" title="Edit Insurance Renewal" />
            {loading ? <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '70vh' }}><Spinner color="primary" /></div> : (
                <Container fluid>
                    <Card>
                        <CardBody>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <H5>Renewal Details</H5>
                                <hr />
                                <Row className="g-3">
                                    <Col md={4}>
                                        <Label>Branch <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="branch_id"
                                            control={control}
                                            rules={{ required: "Branch is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.branch_id}>
                                                        <option value="">Select Branch</option>
                                                        {branches.map((b: any) => (
                                                            <option key={b.id} value={b.id}>{b.branch_name}</option>
                                                        ))}
                                                    </Input>
                                                    <FormFeedback>{errors.branch_id?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Vehicle <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="vehicle_id"
                                            control={control}
                                            rules={{ required: "Vehicle is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.vehicle_id}>
                                                        <option value="">Select Vehicle</option>
                                                        {vehicles.map((v: any) => (
                                                            <option key={v.id} value={v.id}>{v.registration_number} ({v.brand} {v.model})</option>
                                                        ))}
                                                    </Input>
                                                    <FormFeedback>{errors.vehicle_id?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Insurance Company <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="insurance_company"
                                            control={control}
                                            rules={{ required: "Insurance Company is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input {...field} placeholder="Company Name" invalid={!!errors.insurance_company} />
                                                    <FormFeedback>{errors.insurance_company?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={4}>
                                        <Label>Policy Number <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="policy_number"
                                            control={control}
                                            rules={{ required: "Policy Number is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input {...field} placeholder="Policy Number" invalid={!!errors.policy_number} />
                                                    <FormFeedback>{errors.policy_number?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Start Date <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="start_date"
                                            control={control}
                                            rules={{ required: "Start Date is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <DatePicker
                                                        className={`form-control ${errors.start_date ? "is-invalid" : ""}`}
                                                        selected={field.value ? new Date(field.value) : null}
                                                        onChange={(date) => {
                                                            const d = date ? formatDate(date) : "";
                                                            if (watchedExpiry && d && new Date(d) > new Date(watchedExpiry)) {
                                                                toast.error("Start date cannot be after Expiry date");
                                                                return;
                                                            }
                                                            field.onChange(d);
                                                        }}
                                                        placeholderText="YYYY-MM-DD"
                                                    />
                                                    <FormFeedback className="d-block">{errors.start_date?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Expiry Date <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="expiry_date"
                                            control={control}
                                            rules={{ required: "Expiry Date is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <DatePicker
                                                        className={`form-control ${errors.expiry_date ? "is-invalid" : ""}`}
                                                        selected={field.value ? new Date(field.value) : null}
                                                        onChange={(date) => {
                                                            const d = date ? formatDate(date) : "";
                                                            if (watchedStart && d && new Date(d) < new Date(watchedStart)) {
                                                                toast.error("Expiry date cannot be before Start date");
                                                                return;
                                                            }
                                                            field.onChange(d);
                                                        }}
                                                        placeholderText="YYYY-MM-DD"
                                                    // minDate={new Date()}
                                                    />
                                                    <FormFeedback className="d-block">{errors.expiry_date?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={6}>
                                        <Label>Premium Amount <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="premium_amount"
                                            control={control}
                                            rules={{ required: "Premium Amount is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="number" {...field} placeholder="Amount" invalid={!!errors.premium_amount} />
                                                    <FormFeedback>{errors.premium_amount?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Label>Policy Document</Label>
                                        <Controller
                                            name="policy_document"
                                            control={control}
                                            rules={{ required: !docPreview && "Policy Document is required" }}
                                            render={({ field: { value, onChange, ...field } }) => (
                                                <>
                                                    <Input
                                                        type="file"
                                                        {...field}
                                                        accept="image/*,.pdf"
                                                        onChange={(e) => {
                                                            if (e.target.files && e.target.files.length > 0) {
                                                                const file = e.target.files[0];
                                                                if (file.size > 2 * 1024 * 1024) {
                                                                    toast.error("File size must be less than 2MB");
                                                                    e.target.value = ""; // Clear the input
                                                                    onChange(null); // Clear form state
                                                                    return;
                                                                }
                                                            }
                                                            onChange(e.target.files);
                                                        }}
                                                        invalid={!!errors.policy_document}
                                                    />
                                                    <FormFeedback>{errors.policy_document?.message}</FormFeedback>
                                                    {docPreview && !(value instanceof FileList && value.length > 0) && (
                                                        <a href={docPreview} target="_blank" rel="noreferrer" className="d-block mt-2">View Current Document</a>
                                                    )}
                                                    {value instanceof FileList && value.length > 0 && (
                                                        <a href={URL.createObjectURL(value[0])} target="_blank" rel="noreferrer" className="d-block mt-2">View New Document</a>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={12}>
                                        <Label>Remarks</Label>
                                        <Controller
                                            name="remarks"
                                            control={control}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="textarea" {...field} placeholder="Any remarks..." />
                                                </>
                                            )}
                                        />
                                    </Col>
                                </Row>
                                {hasPermission('insurance-renewal.edit') && (
                                    <div className="mt-4">
                                        <Btn color="primary" disabled={loading} type="submit">
                                            {loading ? <>Updating <Spinner size="sm" /></> : "Update"}
                                        </Btn>
                                    </div>
                                )}
                            </Form>
                        </CardBody>
                    </Card>
                </Container>
            )}
            <ToastContainer />
        </div>
    );
}
